/**
 * Shim for `react-native-linear-gradient`.
 *
 * Maps RN's `colors` / `start` / `end` props to a CSS linear-gradient. The
 * angle is derived from the start→end vector so horizontal, vertical and
 * diagonal gradients render as expected.
 */
import React from 'react';
import { View } from '../web/react-native';
import type { StyleProp } from '../web/styleUtils';

export interface LinearGradientProps {
  colors: string[];
  start?: { x: number; y: number };
  end?: { x: number; y: number };
  locations?: number[];
  style?: StyleProp;
  children?: React.ReactNode;
  [key: string]: any;
}

function angleFromVector(start = { x: 0, y: 0 }, end = { x: 0, y: 1 }): number {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  // CSS 0deg points up; convert vector to CSS angle.
  const rad = Math.atan2(dx, -dy);
  const deg = (rad * 180) / Math.PI;
  return (deg + 360) % 360;
}

const LinearGradient = function LinearGradient({
  colors,
  start,
  end,
  locations,
  style,
  children,
  ...rest
}: LinearGradientProps) {
  const angle = angleFromVector(start, end);
  const stops = colors
    .map((c, i) => (locations && locations[i] != null ? `${c} ${locations[i] * 100}%` : c))
    .join(', ');
  const backgroundImage = `linear-gradient(${angle}deg, ${stops})`;
  return (
    <View style={[{ backgroundImage } as any, style]} {...rest}>
      {children}
    </View>
  );
};

export default LinearGradient;
export { LinearGradient };
